import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { WhatsNewNote } from '../types/api';
import { Chip, Modal, SectionLabel } from '../ui';
import styles from './WhatsNewDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  notes: WhatsNewNote[];
  /** Notes to mark "New": what was unread when the panel was opened. */
  newIds: ReadonlySet<string>;
}

// Parsed as a local date: `new Date('2026-10-20')` is UTC midnight, which is the
// previous day anywhere west of Greenwich.
const toDate = (iso: string) => new Date(`${iso}T00:00:00`);
const monthLabel = (iso: string) =>
  toDate(iso).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const dayLabel = (iso: string) =>
  toDate(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** Notes grouped by month, keeping their newest-first order. */
function byMonth(notes: WhatsNewNote[]): [string, WhatsNewNote[]][] {
  const groups = new Map<string, WhatsNewNote[]>();
  for (const note of notes) {
    const month = monthLabel(note.date);
    groups.set(month, [...(groups.get(month) ?? []), note]);
  }
  return [...groups];
}

/** The What's new panel: every note this person may read, newest first. */
export default function WhatsNewDialog({ open, onClose, notes, newIds }: Props) {
  return (
    <Modal open={open} onClose={onClose} title="What's new" size="lg">
      {notes.length === 0 ? (
        <p className={styles.empty}>Nothing here yet.</p>
      ) : (
        byMonth(notes).map(([month, group]) => (
          <section key={month} className={styles.month}>
            <SectionLabel as="h3" rule>
              {month}
            </SectionLabel>
            {group.map((note) => (
              <article key={note.id} className={styles.note}>
                <time className={styles.date} dateTime={note.date}>
                  {dayLabel(note.date)}
                </time>
                <div className={styles.content}>
                  <h4 className={styles.title}>
                    {note.title}
                    {newIds.has(note.id) && <span className={styles.new}>New</span>}
                  </h4>
                  {(note.product_name || note.internal) && (
                    <div className={styles.tags}>
                      {note.product_name && <Chip size="sm">{note.product_name}</Chip>}
                      {note.internal && (
                        <Chip size="sm" tone="accent" title="Only DHI colleagues see this note">
                          Internal
                        </Chip>
                      )}
                    </div>
                  )}
                  <div className={styles.body}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ node: _node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" />
                        ),
                      }}
                    >
                      {note.body}
                    </ReactMarkdown>
                  </div>
                </div>
              </article>
            ))}
          </section>
        ))
      )}
    </Modal>
  );
}
